import { computed, type ComputedRef } from "vue";
import type { ComboboxTriggerProps } from "./combobox.types";
import { useCombobox } from "./useCombobox";
import { comboboxTriggerClasses } from "./combobox.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";

export interface UseComboboxTriggerOptions {
  props: Readonly<ComboboxTriggerProps>;
}

export interface UseComboboxTriggerReturn {
  classes: ComputedRef<ReturnType<typeof comboboxTriggerClasses>>;
  combobox: ReturnType<typeof useCombobox>;
  handleClick: (event: MouseEvent) => void;
}

export const useComboboxTrigger = (options: UseComboboxTriggerOptions): UseComboboxTriggerReturn => {
  const { props } = options;
  const combobox = useCombobox();

  const classes = computed(() =>
    mergeClassProp(
      comboboxTriggerClasses({
        disabled: combobox.comboboxProps.value?.disabled ? "true" : "false",
      }),
      props.class,
    ),
  );

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (combobox.comboboxProps.value?.disabled) return;
    const wasOpen = combobox.isOpen.value;
    combobox.toggle();

    // Focus the input after opening so the user can type immediately
    if (!wasOpen) {
      const comboboxEl = combobox.comboboxElement.value;
      if (comboboxEl) {
        const comboboxInput = comboboxEl.querySelector("e-combobox-input");
        const input = comboboxInput?.shadowRoot?.querySelector("input") as HTMLElement | null;
        input?.focus();
      }
    }
  };

  return {
    classes,
    combobox,
    handleClick,
  };
};
